import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getGridLayoutForDesktop, getGridsOfFormFields } from "../../../commonFunctions/formFunc";
import Loader from "../../../Layouts/Loader/Loader";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
// import MultiRadio from "../../../components/FormComponents/MultiRadio";
import { useNavigate, useParams } from "react-router-dom";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import Textarea from "../../../components/FormComponents/Textarea";
import { useAppBlocksContext } from "../../../context/AppBlocksContext";
import { useFormContext } from "../../../context/FormContext";
import { useTableContext } from "../../../context/TableContext";
import { useTreeContext } from "../../../context/TreeDataContext";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { getLayoutDataOfDetailviewElements } from "../../../utils/InitialRenderingData";
import FormCustomFunctions from "../CustomFunctions/FormCustomFunctions";
import FieldsGeneraterBot from "../FieldsGeneraterBot";
import FormGeneralSetting from "../FormGeneralSetting";
import { FormLayoutWithEdit } from "../FormSection";
import RightSideFieldsBar from "../RightSideFieldsBar";
import FormStaticData from "./FormStaticData";
import { processLayout } from "../../../utils/GridLayoutFunc";
import { getElementIds } from "../../FlexibleLayout/utils";
import { toastSuccess } from "../../../utils/toastUtils";
import { getFolderFileById } from "../../../utils/treeData/folderEditorUtils";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import OnChangeCustomJs from "../OnChangeCustomJs/OnChangeCustomJs";
import InitalCustomJs from "../InitialCustomJS/InitialCustomJs";

const SMFormSection = () => {
  const [isLoader, setIsLoader] = useState(true);
  const [error, setError] = useState(null); // ✅ new error state
  const navigate = useNavigate();
  const { formsExplorerData } = useTreeContext();

  const {
    renderFieldsData,
    setRenderFieldsArray,
    formSectionsData,
    formSettingData,
    setSelectedForm,
    setFormActivityData
  } = useFormContext();
  const { setLayout, layoutData, nestedGridData } = useAppBlocksContext();
  const { getTableTypeData } = useTableContext();
  const { formId, smId } = useParams();
  const [tableName, setTableName] = useState("");
  const [tableType, setTableType] = useState("");

  const fetchActivityForm = async () => {
    setIsLoader(true);
    setError(null);

    try {
      formSectionsData.current = [];
      formSettingData.current = {};

      // Fetch activity form data
      const { data } = await axiosInstance.get(`/state-machine/${smId}/activities/${formId}`);

      // Fetch table info
      const tableData = await getTableTypeData("statemachine", smId);
      const { table_type, table_name = "" } = tableData.data.table;
      setTableType(table_type);
      setTableName(table_name);

      // Prepare fields and sections
      renderFieldsData.current = {};
      const updatedSections = data.form.sections.map((sec) => {
        const fields = sec.fields;
        const newDesktopLayout = getGridLayoutForDesktop(fields, sec.desktop_layout || {});
        const newMobileLayout = getGridLayoutForDesktop(fields, sec.mobile_layout || {});
        const newTabletLayout = getGridLayoutForDesktop(fields, sec.tablet_layout || {});
        const mappedFields = Object.fromEntries(fields.map((field) => [field.id, { ...field }]));
        renderFieldsData.current = { ...renderFieldsData.current, ...mappedFields };

        return {
          ...sec,
          desktop_layout: newDesktopLayout,
          mobile_layout: newMobileLayout,
          tablet_layout: newTabletLayout
        };
      });

      // Deep clone and update sections
      const newUpdatedData = { ...data, form: { ...data.form, sections: updatedSections } };

      setFormActivityData(newUpdatedData);

      const detailsElements = newUpdatedData.details_view?.elements || [];
      const {
        layout: staticLayout,
        layoutData: staticLayoutData,
        nestedGridData: staticNestedGridData
      } = getLayoutDataOfDetailviewElements(detailsElements, tableData);

      setLayout(staticLayout);
      layoutData.current = staticLayoutData;
      nestedGridData.current = staticNestedGridData;

      // Update refs for form settings and sections
      formSettingData.current = newUpdatedData.form;
      formSectionsData.current = newUpdatedData.form.sections;

      const gridArray = getGridsOfFormFields(newUpdatedData.form.sections[0]?.fields || []);
      setRenderFieldsArray(gridArray);
    } catch (err) {
      handleApiError(err);
      setError("Failed to load form. Please try again later.");
    } finally {
      setIsLoader(false);
    }
  };

  const initialRendering = async () => {
    const fileId = `${smId}/statemachine/${formId}`;
    const file = getFolderFileById(formsExplorerData, fileId);
    if (file) {
      setSelectedForm({
        id: fileId,
        name: file.name,
        parentData: file.parentData,
        type: file.type
      });
    } else {
      setError("Form not found.");
    }
  };

  useEffect(() => {
    initialRendering();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smId, formId]);

  useEffect(() => {
    fetchActivityForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smId, formId]);

  if (isLoader) return <Loader />;

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-50 rounded-md">
        <p>{error}</p>
        <button onClick={() => navigate("../")} className="mt-2 px-4 py-2 bg-red-500 text-white rounded">
          Go Back
        </button>
      </div>
    );
  }

  return <SmFormEditor tableName={tableName} tableType={tableType} />;
};
const SmFormEditor = ({ tableName, tableType }) => {
  const [selectedTab, setSelectedTab] = useState("form");
  const [isAiModalOpen, setIsAiModalOpen] = useState(false);
  const { formId, appId, orgId, smId } = useParams();
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  const [dragFromOutside, setDragFromOutside] = useState(false);
  const [activeMode, setActiveMode] = useState("edit");
  const navigate = useNavigate();

  const {
    renderFieldsArray,
    formSectionsData,
    formSettingData,
    selectedForm,
    saveCurrentSectionInData,
    selectedSectionData,
    formActvityData
  } = useFormContext();
  const { layout, layoutData, nestedGridData } = useAppBlocksContext();

  const onChangeActiveMode = (value) => {
    setActiveMode(value);
  };

  const handleChangeTab = (tab) => {
    if (tab === "custom functions" || tab === "static data") {
      formSectionsData.current[selectedSectionData].fields = saveCurrentSectionInData();
    }
    setSelectedTab(tab);
  };

  const handleDragStart = useCallback(
    (e, name) => {
      e.dataTransfer.setData("type", name);
      setDragFromOutside(true);
    },
    [setDragFromOutside]
  );

  const memoizedRightSideFieldsBar = useMemo(() => {
    return <RightSideFieldsBar from={"form"} handleDragStart={handleDragStart} />;
  }, [handleDragStart]);

  const openAiFormModal = () => {
    setIsAiModalOpen(true);
  };

  const navigateToPositionFormScreen = () => {
    navigate(`/organization/${orgId}/app/${appId}/smform/${smId}/form/${formId}/advancededit`);
  };

  let tabList =
    tableType === "entity"
      ? ["form", "custom functions", "initial customJs"]
      : ["form", "static data", "custom functions", "onchange customJs", "initial customJs"];

  const formDetailviewConfig = () => {
    let { elements } = processLayout(layoutData, nestedGridData, layout);
    return {
      form_backend_validation: formActvityData.form_backend_validation,
      custom_js: formActvityData.custom_js,
      details_view: {
        ...formActvityData.details_view,
        position_config: {
          ...formActvityData.details_view?.position_config,
          desktop: {
            ...formActvityData.details_view?.position_config?.desktop,
            position_config_ids: getElementIds(formActvityData.details_view?.position_config?.desktop?.config || [])
          },
          mobile: {
            ...formActvityData.details_view?.position_config?.mobile,
            position_config_ids: getElementIds(formActvityData.details_view?.position_config?.mobile?.config || [])
          },
          tablet: {
            ...formActvityData.details_view?.position_config?.tablet,
            position_config_ids: getElementIds(formActvityData.details_view?.position_config?.tablet?.config || [])
          }
        },
        elements: tableType !== "entity" ? elements || [] : []
      },
      onchange_custom_js: formActvityData?.onchange_custom_js || [],
      initial_custom_js: formActvityData?.initial_custom_js || ""
    };
  };

  // build function (always reads from refs + props/state)
  const buildCurrentConfig = () => {
    if (selectedSectionData != null) {
      formSectionsData.current[selectedSectionData].fields = saveCurrentSectionInData(renderFieldsArray);
    }

    return {
      form: {
        name: formSettingData.current?.name,
        sections: formSectionsData.current || [],
        settings: {
          ...formSettingData.current?.settings,
          ignore_local_storage: formSettingData.current.settings.ignore_local_storage
        },
        submit_button: formSettingData.current?.submit_button,
        reset_button: formSettingData.current?.reset_button,
        style: formSettingData.current?.style,
        is_positioned_form: formSettingData.current?.is_positioned_form
      },
      activity: formDetailviewConfig()
    };
  };

  const [currentConfig, setCurrentConfig] = useState(buildCurrentConfig);

  // dirty checker hook
  const { isDirty, markClean } = useDirtyChecker(currentConfig);

  // rebuild config whenever *reactive inputs* change
  useEffect(() => {
    setCurrentConfig(buildCurrentConfig());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, layoutData, nestedGridData, renderFieldsArray, formActvityData, tableType]);

  const handleSaveForm = async () => {
    try {
      setIsSaving(true);

      formSectionsData.current[selectedSectionData].fields = saveCurrentSectionInData(renderFieldsArray);

      const config = {
        form: {
          name: formSettingData.current.name,
          sections: formSectionsData.current,
          settings: {
            ...formSettingData.current.settings,
            ignore_local_storage: formSettingData.current.settings.ignore_local_storage
          },
          submit_button: formSettingData.current.submit_button,
          reset_button: formSettingData.current.reset_button,
          style: formSettingData.current.style,
          is_positioned_form: formSettingData.current.is_positioned_form
        },
        activity: formDetailviewConfig()
      };

      await axiosInstance.put(`/state-machine/${smId}/activities/${formId}`, config.activity);

      const { message } = await axiosInstance.put(`/state-machine/${smId}/activity/${formId}/form`, config.form);

      toastSuccess(message);
      markClean();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="form_outer_body w_100">
        <div className="form_outer_section">
          <div className="form_sm_section">
            <GenericActionBar
              path={
                <div>
                  <span>{tableName}</span> → <span>{selectedForm.name}</span>
                </div>
              }
              actions={[
                {
                  key: "settings",
                  icon: "settings",
                  title: "Settings",
                  onClick: () => setIsModalOpen(true)
                },
                {
                  key: "json",
                  icon: "data_object",
                  title: "JSON",
                  onClick: () => setIsJsonModalOpen(true)
                },
                {
                  key: "save",
                  icon: "save",
                  title: isSaving ? "Saving..." : "Save",
                  onClick: isSaving ? undefined : handleSaveForm
                }
              ]}
              options={{
                save: {
                  className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
                }
              }}
            />
            <div
              className="d_flex align_center bg_gray w_100 border_bottom px_2 justify_between"
              style={{
                zIndex: "5",
                height: "48px"
              }}
            >
              <div className="page_tab_container h_100">
                {tabList.map((tab) => (
                  <button
                    key={tab}
                    className={`page_tab h_100 ${selectedTab === tab && "active"}`}
                    onClick={() => handleChangeTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="flex gap_2">
                <div
                  className="d_flex align_center gap_2 font_weight_semibold hover_border_bottom cursor_pointer"
                  onClick={navigateToPositionFormScreen}
                >
                  <span>Advance Design</span>{" "}
                  <MaterialIcon name="north_east" className="font_weight_medium font_size_medium" />
                </div>
              </div>
            </div>
            {selectedTab === "form" && (
              <div>
                <div className="main_formrenderer_section" style={{ height: "calc(100% - 136px)" }}>
                  <FormLayoutWithEdit
                    dragFromOutside={dragFromOutside}
                    setDragFromOutside={setDragFromOutside}
                    activeMode={activeMode}
                    tableID={smId}
                    onChangeActiveMode={onChangeActiveMode}
                  />
                  {activeMode === "edit" && (
                    <div className="ai_bot_icon" onClick={openAiFormModal}>
                      <MaterialIcon name="smart_toy" />
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedTab === "static data" && <FormStaticData />}
            {selectedTab === "custom functions" && <FormCustomFunctions />}
            {selectedTab === "onchange customJs" && <OnChangeCustomJs />}
            {selectedTab === "initial customJs" && <InitalCustomJs />}
          </div>
          {selectedTab === "form" && activeMode === "edit" && (
            <div className="rightsidebar">{memoizedRightSideFieldsBar}</div>
          )}
        </div>
      </div>
      <BuilderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} modalName="Edit Form Setting">
        <FormGeneralSetting formSettingData={formSettingData} setIsModalOpen={setIsModalOpen} />
      </BuilderModal>
      <BuilderModal isOpen={isJsonModalOpen} onClose={() => setIsJsonModalOpen(false)} modalName="Form JSON">
        <FormJsonModal
          formSettingData={formSettingData}
          formSectionsData={formSectionsData}
          saveCurrentSectionInData={saveCurrentSectionInData}
          renderFieldsArray={renderFieldsArray}
          selectedSectionData={selectedSectionData}
        />
      </BuilderModal>
      <BuilderModal isOpen={isAiModalOpen} onClose={() => setIsAiModalOpen(false)} modalName={"Fields Generator"}>
        <FieldsGeneraterBot setIsAiModalOpen={setIsAiModalOpen} />
      </BuilderModal>
    </>
  );
};

const FormJsonModal = ({
  formSettingData,
  formSectionsData,
  saveCurrentSectionInData,
  renderFieldsArray,
  selectedSectionData
}) => {
  const [jsonData, setJsonData] = useState("");
  useEffect(() => {
    formSectionsData.current[selectedSectionData].fields = saveCurrentSectionInData(renderFieldsArray);

    const tempObj = {
      name: formSettingData.current.name,
      sections: formSectionsData.current,
      settings: formSettingData.current.settings
    };
    setJsonData(JSON.stringify(tempObj, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Textarea value={jsonData} style={{ width: "100%", height: "50vh" }} disabled />
    </div>
  );
};

export default SMFormSection;
